import type { ModulatorLinkCsId } from "~domain/devices/models/links/link-model.ts";
import type { SiteId } from "~domain/sites/models/site-model.ts";
import type { SitePhotoId } from "~domain/sites/models/site-photo-model.ts";

import { initContract } from "@ts-rest/core";
import { z } from "zod";
import { backofficeErrorSchema } from "~infrastructure/api/backoffice/contract/global/responses/global-backoffice-responses.ts";
import { searchSiteRequestBodySchema } from "~infrastructure/api/backoffice/contract/site/requests/search-site-request-body.ts";
import {
  autoDiagDeviceDtoSchema,
  backofficeFindAutodiagDiagnosisResponseSchema,
} from "~infrastructure/api/backoffice/contract/site/responses/backoffice-find-autodiag-diagnosis-response.ts";
import { backofficeFindLastHardwareConfigurationResponse } from "~infrastructure/api/backoffice/contract/site/responses/backoffice-find-last-hardware-configuration-response.ts";
import { backofficeFindLastSoftwareUpdateResponse } from "~infrastructure/api/backoffice/contract/site/responses/backoffice-find-last-software-update-response.ts";
import { backofficeFindSiteConstraintsSummaryResponseSchema } from "~infrastructure/api/backoffice/contract/site/responses/backoffice-find-site-constraints-summary-response.ts";
import { backofficeFindSiteEventTreeResponseSchema } from "~infrastructure/api/backoffice/contract/site/responses/backoffice-find-site-event-tree-response.ts";
import { backofficeFindSiteLastButtonEventResponseSchema } from "~infrastructure/api/backoffice/contract/site/responses/backoffice-find-site-last-button-event-response.ts";
import { backofficeFindSiteLastCommunicationResponse } from "~infrastructure/api/backoffice/contract/site/responses/backoffice-find-site-last-communication-response.ts";
import { backofficeFindSiteModulatorsResponseSchema } from "~infrastructure/api/backoffice/contract/site/responses/backoffice-find-site-modulators-response.ts";
import { backofficeFindSiteModulatorsWorkingConditionResponseSchema } from "~infrastructure/api/backoffice/contract/site/responses/backoffice-find-site-modulators-working-condition-response.ts";
import { backofficeFindSiteNextModulationAvailabilityResponseSchema } from "~infrastructure/api/backoffice/contract/site/responses/backoffice-find-site-next-modulation-availability-response.ts";
import { backofficeFindSitePhotoResponseSchema } from "~infrastructure/api/backoffice/contract/site/responses/backoffice-find-site-photo-response.ts";
import { backofficeFindSitePhotosResponseSchema } from "~infrastructure/api/backoffice/contract/site/responses/backoffice-find-site-photos-response.ts";
import { backofficeFindSitePilotsLastConnectionDatesResponseSchema } from "~infrastructure/api/backoffice/contract/site/responses/backoffice-find-site-pilots-last-connection-dates-response.ts";
import { backofficeFindSitePilotsResponseSchema } from "~infrastructure/api/backoffice/contract/site/responses/backoffice-find-site-pilots-response.ts";
import { backofficeFindSiteResponseSchema } from "~infrastructure/api/backoffice/contract/site/responses/backoffice-find-site-response.ts";
import { backofficeSiteSearchResponseSchema } from "~infrastructure/api/backoffice/contract/site/responses/backoffice-site-search-response.ts";

const c = initContract();

export const sitesBackofficeContract = c.router({
  findSite: {
    method: "GET",
    path: "/api/site/:siteId",
    pathParams: z.object({
      siteId: z.number().transform((s) => s as SiteId),
    }),
    responses: {
      200: backofficeFindSiteResponseSchema,
      404: backofficeErrorSchema,
    },
  },
  findSiteModulators: {
    method: "GET",
    path: "/api/site/:siteId/modulators",
    pathParams: z.object({
      siteId: z.number().transform((s) => s as SiteId),
    }),
    responses: {
      200: backofficeFindSiteModulatorsResponseSchema,
      404: backofficeErrorSchema,
    },
  },
  findSitePilots: {
    method: "GET",
    path: "/api/site/:siteId/mombox",
    pathParams: z.object({
      siteId: z.number().transform((s) => s as SiteId),
    }),
    responses: {
      200: backofficeFindSitePilotsResponseSchema,
      404: backofficeErrorSchema,
    },
  },
  findSiteModulatorsWorkingCondition: {
    method: "GET",
    path: "/api/site/:siteId/modulator/working-condition",
    pathParams: z.object({
      siteId: z.number().transform((s) => s as SiteId),
    }),
    responses: {
      200: backofficeFindSiteModulatorsWorkingConditionResponseSchema,
    },
  },
  findSiteLastButtonEvent: {
    method: "GET",
    path: "/api/site/:siteId/last-button-event",
    pathParams: z.object({
      siteId: z.number().transform((s) => s as SiteId),
    }),
    responses: {
      200: backofficeFindSiteLastButtonEventResponseSchema,
    },
  },
  findSiteNextModulationAvailability: {
    method: "GET",
    path: "/api/modulation/site/:siteId/next-availability",
    pathParams: z.object({
      siteId: z.number().transform((s) => s as SiteId),
    }),
    responses: {
      200: backofficeFindSiteNextModulationAvailabilityResponseSchema,
    },
  },
  findSiteConstraints: {
    method: "GET",
    path: "/api/site/:siteId/constraints-summary",
    pathParams: z.object({
      siteId: z.number().transform((s) => s as SiteId),
    }),
    responses: {
      200: backofficeFindSiteConstraintsSummaryResponseSchema,
    },
  },
  findSiteLastCommunication: {
    method: "GET",
    path: "/api/site/:siteId/event/last-communication",
    pathParams: z.object({
      siteId: z.number().transform((s) => s as SiteId),
    }),
    responses: {
      200: backofficeFindSiteLastCommunicationResponse,
    },
  },
  findSiteLastHardwareConfiguration: {
    method: "GET",
    path: "/api/site/:siteId/event/last-hardware-configuration",
    pathParams: z.object({
      siteId: z.number().transform((s) => s as SiteId),
    }),
    responses: {
      200: backofficeFindLastHardwareConfigurationResponse,
    },
  },
  findSiteLastSoftwareUpdate: {
    method: "GET",
    path: "/api/mombox/:siteId/last-software-update",
    pathParams: z.object({
      siteId: z.number().transform((s) => s as SiteId),
    }),
    responses: {
      200: backofficeFindLastSoftwareUpdateResponse,
    },
  },
  findAutodiagDiagnosis: {
    method: "GET",
    path: "/api/autodiag/site/:siteId",
    pathParams: z.object({
      siteId: z.number().transform((s) => s as SiteId),
    }),
    responses: {
      200: backofficeFindAutodiagDiagnosisResponseSchema,
    },
  },
  startLinkAutoDiag: {
    method: "PUT",
    path: "/api/autodiag/site/:siteId/test/link/:csLinkId",
    pathParams: z.object({
      siteId: z.number().transform((s) => s as SiteId),
      csLinkId: z.number().transform((s) => s as ModulatorLinkCsId),
    }),
    body: z.void(),
    responses: {
      200: autoDiagDeviceDtoSchema,
    },
  },
  autodiagRefreshAllMyVAppliances: {
    method: "PUT",
    path: "/api/myv/programming/site/:siteId/refreshAllAppliances",
    pathParams: z.object({
      siteId: z.number().transform((s) => s as SiteId),
    }),
    body: z.void(),
    responses: {
      200: autoDiagDeviceDtoSchema,
    },
  },
  findSitePhotos: {
    method: "GET",
    path: "/api/site/:siteId/photosList",
    pathParams: z.object({
      siteId: z.number().transform((s) => s as SiteId),
    }),
    responses: {
      200: backofficeFindSitePhotosResponseSchema,
    },
  },
  findSitePhoto: {
    method: "GET",
    path: "/api/photos/:photoId",
    pathParams: z.object({
      photoId: z.string().transform((s) => s as SitePhotoId),
    }),
    query: z.object({
      format: z.enum(["THUMBNAIL", "HD"]).catch("THUMBNAIL"),
    }),
    responses: {
      200: backofficeFindSitePhotoResponseSchema,
    },
  },
  findSitePilotsLastConnectionDates: {
    method: "GET",
    path: "/api/site/:siteId/mombox-last-connection-date",
    pathParams: z.object({
      siteId: z.number().transform((s) => s as SiteId),
    }),
    responses: {
      200: backofficeFindSitePilotsLastConnectionDatesResponseSchema,
    },
  },
  searchSites: {
    method: "POST",
    path: "/api/site/search",
    body: searchSiteRequestBodySchema,
    responses: {
      200: backofficeSiteSearchResponseSchema.array(),
    },
  },
  findSiteEventTree: {
    method: "GET",
    path: "/api/site/:siteId/events/tree",
    pathParams: z.object({
      siteId: z.number().transform((s) => s as SiteId),
    }),
    responses: {
      200: backofficeFindSiteEventTreeResponseSchema,
    },
  },
});
